import { Injectable, inject, PLATFORM_ID, signal, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

/** Cookie consent level. Essential = only strictly necessary; All = includes preferences/analytics. */
export type ConsentLevel = 'essential' | 'all';

/** Granular consent per category. Essential is always true. */
export interface ConsentPreferences {
  essential: true; // always true, locked
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
  essential: true,
  functional: true,
  analytics: false,
  marketing: false,
};

const CONSENT_COOKIE = 'rp_consent';
const CONSENT_DAYS = 365;

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);

  /** User's consent preferences, or null if not yet chosen. */
  private _preferences = signal<ConsentPreferences | null>(null);

  readonly preferences = this._preferences.asReadonly();
  readonly hasConsented = computed(() => this._preferences() !== null);
  readonly showBanner = computed(() => !this.hasConsented() && isPlatformBrowser(this.platformId));

  /** Legacy: 'essential' | 'all' for backward compat and simple checks. */
  readonly consent = computed<ConsentLevel | null>(() => {
    const p = this._preferences();
    if (!p) return null;
    return p.functional && p.analytics && p.marketing ? 'all' : 'essential';
  });

  /** Whether customise modal is open. */
  readonly customiseOpen = signal(false);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const stored = this.readConsentFromCookie();
      if (stored) this._preferences.set(stored);
    }
  }

  acceptAll(): void {
    this.setPreferences({
      ...DEFAULT_PREFERENCES,
      functional: true,
      analytics: true,
      marketing: true,
    });
  }

  acceptEssential(): void {
    this.setPreferences({
      ...DEFAULT_PREFERENCES,
      functional: false,
      analytics: false,
      marketing: false,
    });
  }

  openCustomise(): void {
    this.customiseOpen.set(true);
  }

  closeCustomise(): void {
    this.customiseOpen.set(false);
  }

  openCookieSettings(): void {
    this.router.navigate(['/legal/cookies'], { queryParams: { settings: '1' } });
  }

  /** Save custom preferences from the modal. */
  setPreferences(prefs: ConsentPreferences): void {
    if (typeof document === 'undefined') return;
    this._preferences.set(prefs);
    this.customiseOpen.set(false);
    const json = JSON.stringify(prefs);
    const expires = new Date(Date.now() + CONSENT_DAYS * 86400000).toUTCString();
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(json)}; path=/; expires=${expires}; SameSite=Lax`;
  }

  /** Called from cookie policy page when user changes preference. */
  setConsent(level: ConsentLevel): void {
    if (level === 'all') {
      this.acceptAll();
    } else {
      this.acceptEssential();
    }
  }

  /** Withdraw consent (show banner again on next visit). */
  withdrawConsent(): void {
    if (typeof document === 'undefined') return;
    this._preferences.set(null);
    document.cookie = `${CONSENT_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  /** Get current preferences for editing in modal. Defaults for first-time. */
  getEditablePreferences(): ConsentPreferences {
    const p = this._preferences();
    return p ? { ...p } : { ...DEFAULT_PREFERENCES };
  }

  private readConsentFromCookie(): ConsentPreferences | null {
    if (typeof document === 'undefined') return null;
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, ...rest] = cookie.split('=');
      const value = rest.join('=').trim();
      if (key !== CONSENT_COOKIE) continue;
      // Legacy: "essential" or "all"
      if (value === 'essential') {
        return { ...DEFAULT_PREFERENCES, functional: false, analytics: false, marketing: false };
      }
      if (value === 'all') {
        return { ...DEFAULT_PREFERENCES, functional: true, analytics: true, marketing: true };
      }
      try {
        const parsed = JSON.parse(decodeURIComponent(value)) as Partial<ConsentPreferences>;
        if (parsed && typeof parsed === 'object') {
          return {
            ...DEFAULT_PREFERENCES,
            functional: !!parsed.functional,
            analytics: !!parsed.analytics,
            marketing: !!parsed.marketing,
            essential: true,
          };
        }
      } catch {}
    }
    return null;
  }
}

